/**
 * @fileoverview Contact management actions, hooks, and analytics
 * @module ContactActions
 */

import apiClient from "../utils/apiClient";
import useSWR from "swr";
import queryString from "query-string";
import { atom } from "jotai";
/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */
/**
 * Hook to obtain list of contacts
 * @function
 * @name useContacts
 * @memberof module:ContactActions
 * @param {Object} params - Initial params for each contact
 * @returns {SWRObject} SWR Object containing contacts data
 */
export const useContacts = (params) => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);

  const query = queryString.stringify(params, { arrayFormat: "comma" });

  const { data, error, mutate } = useSWR(`/list_contact/?${query}`, fetcher);

  return { data, isLoading: !error && !data, error, mutate };
};
/**
 * Default parameters for contact queries
 * @constant
 * @name initialParams
 * @memberof module:ContactActions
 * @type {Object}
 */

export const initialParams = {
  page: 1,
  status: 1,
};

export const contactsParamsAtom = atom(initialParams);
/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 */

/**
 * Hook that returns analytics for WhatsApp events
 * @function
 * @name UseAnalytics
 * @memberof module:ContactActions
 * @param {Object} params - Events for the WhatsApp data
 * @returns {SWRObject} SWR Object containing analytics data
 */
export function UseAnalytics(params) {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);

  const { data, error } = useSWR(
    `/analytics/events/?${queryString.stringify(params)}`,
    fetcher
  );

  return { data, error };
}

/**
 * Hook that returns the count of managed leads
 * @function
 * @name UseManagedLeadsAnalytics
 * @memberof module:ContactActions
 * @param {Object} params - Events for the managed leads
 * @returns {SWRObject} SWR Object containing managed leads count
 */
export function UseManagedLeadsAnalytics(params) {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);

  const { data, error } = useSWR(
    `/analytics/managed_leads/?${queryString.stringify(params)}`,
    fetcher
  );

  return { data, error };
}

/**
 * Updates the status of a contact
 * @function
 * @name updateContact
 * @memberof module:ContactActions
 * @param {number} contactID - ID for the contact
 * @param {Object} data - Values of the contact status to update
 * @returns {Promise<void>}
 */

export async function updateContact(contactID, data) {
  try {
    await apiClient.patch(`contact/${contactID}`, data);
  } catch (err) {
    $log.error(err);
  }
}

/**
 * @typedef {Object} SWRObject
 * @property {object} data - The pipeline data
 * @property {boolean} error - Error state from the SWR call
 */

/**
 * Hook to fetch available pipelines for contacts
 * @function
 * @name usePipelines
 * @memberof module:ContactActions
 * @returns {SWRObject} SWR object with pipeline data
 */
export const usePipelines = () => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);

  const { data, error } = useSWR("/pipelines/", fetcher);

  return { data: data?.results, error };
};

/**
 * Hook to fetch comments for a specific contact
 * @function
 * @name useComments
 * @memberof module:ContactActions
 * @param {string|number} contactId - The ID of the contact to fetch comments for
 * @returns {SWRObject} SWR object with comments data, loading state, error, and mutate function
 */
export const useComments = (contactId) => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);

  const { data, error, mutate } = useSWR(
    `/contacts/${contactId}/comments/`,
    fetcher
  );

  return { data: data?.results, isLoading: !error && !data, error, mutate };
};

/**
 * Hook to fetch pipeline history for a specific contact
 * @function
 * @name useContactPipelineHistory
 * @memberof module:ContactActions
 * @param {string|number} contactId - The ID of the contact to fetch pipeline history for
 * @returns {SWRObject} SWR object with pipeline history data, loading state, error, and mutate function
 */
export const useContactPipelineHistory = (contactId) => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);

  const { data, error, mutate } = useSWR(
    `/contacts/${contactId}/pipeline-history/`,
    fetcher
  );

  return { data: data?.results, isLoading: !error && !data, error, mutate };
};

/**
 * Creates a new standalone contact
 * @function
 * @name createContact
 * @memberof module:ContactActions
 * @param {Object} data - Contact data to create the new contact
 * @param {string} data.name - Contact's name
 * @param {string} data.email - Contact's email address
 * @param {string} [data.phone] - Contact's phone number
 * @returns {Promise<Object|undefined>} The created contact response or undefined if error
 */
export const createContact = async (data) => {
  try {
    const res = await apiClient.post(
      "/properties/create_standalone_contact/",
      data
    );
    return res;
  } catch (err) {
    $log.error(err);
  }
};
